import React, { useEffect, useMemo, useState, useContext } from 'react'

import LoadingContext from '../../context/Loading/Loading'

import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Tab from 'react-bootstrap/Tab'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Alert from 'react-bootstrap/Alert'

import {
  getEntryPoints,
  getScanners,
  getOrgVenues,
  createOrEditEntryPoint,
  createOrEditScanner,
} from '../../utilities/api'
import AuthService from '../../utilities/services/auth.service'
import UserContext from '../../context/User/User'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { checkPermission } from '../../utilities/helpers'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { Spinner } from '../LoadingContainer/Spinner'
import EntryPointsTable from './EntryPointsTable'
import ScannersTable from './ScannersTable'

const EMPTY_ENTRY_POINT = {
  id: '',
  name: '',
  venueId: '',
}

const EMPTY_SCANNER = {
  id: '',
  name: '',
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  entry_point_id: '',
}

const slugifyScannerEmailPart = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')

const splitScannerName = (name) => {
  const trimmed = String(name || '').trim()
  if (!trimmed) {
    return { firstName: '', lastName: '' }
  }

  const parts = trimmed.split(/\s+/)
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: parts[0] }
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  }
}

const buildScannerEmail = (name, organizationName) => {
  const localPart = slugifyScannerEmailPart(name)
  const orgPart = slugifyScannerEmailPart(organizationName)

  if (!localPart || !orgPart) {
    return ''
  }

  return `${localPart}@${orgPart}.scan`
}

export default function ScannersWrapper({ organization }) {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const currentUser = AuthService.getUser()?.user
  const isSuperAdmin = Boolean(
    currentUser?.superAdmin ||
    organization?.members?.some(
      (member) => member?.uuid === currentUser?.uuid && member?.superAdmin
    )
  )
  const hasPermission =
    isSuperAdmin ||
    (orgPermissionsReady &&
      checkPermission(
        getPermissions(),
        ORGANIZATION_PERMISSION_SLUGS.MANAGE_SCANNERS
      ))

  const [entryPoints, setEntryPoints] = useState([])
  const [scanners, setScanners] = useState([])
  const [venues, setVenues] = useState([])
  const [key, setKey] = useState('entry-points')
  const [showEntryPointModal, setShowEntryPointModal] = useState(false)
  const [showScannerModal, setShowScannerModal] = useState(false)
  const [entryPoint, setEntryPoint] = useState(EMPTY_ENTRY_POINT)
  const [scanner, setScanner] = useState(EMPTY_SCANNER)
  const [submitError, setSubmitError] = useState('')
  const [isSavingEntryPoint, setIsSavingEntryPoint] = useState(false)
  const [isSavingScanner, setIsSavingScanner] = useState(false)
  const [entryPointValidationAttempted, setEntryPointValidationAttempted] =
    useState(false)
  const [scannerValidationAttempted, setScannerValidationAttempted] =
    useState(false)

  const isEditingEntryPoint = Boolean(entryPoint.id)
  const isEditingScanner = Boolean(scanner.id)

  const entryPointFieldErrors = useMemo(() => {
    const errors = {}

    if (!entryPoint.name?.trim()) {
      errors.name = 'Entry point name is required.'
    }

    if (!entryPoint.venueId) {
      errors.venueId = 'Select a venue.'
    }

    return errors
  }, [entryPoint])

  const scannerFieldErrors = useMemo(() => {
    const errors = {}

    if (isEditingScanner) {
      if (!scanner.firstName?.trim()) {
        errors.firstName = 'First name is required.'
      }

      if (!scanner.lastName?.trim()) {
        errors.lastName = 'Last name is required.'
      }

      if (!scanner.email?.trim()) {
        errors.email = 'Email is required.'
      }
    } else {
      if (!scanner.name?.trim()) {
        errors.name = 'Name is required.'
      } else if (!scanner.email?.trim()) {
        errors.email = 'Enter a valid name to generate an email address.'
      }
    }

    if (!scanner.entry_point_id) {
      errors.entry_point_id = 'Select an entry point.'
    }

    if (!isEditingScanner && !scanner.password?.trim()) {
      errors.password = 'Password is required.'
    }

    return errors
  }, [isEditingScanner, scanner])

  const isEntryPointFormValid = Object.keys(entryPointFieldErrors).length === 0
  const isScannerFormValid = Object.keys(scannerFieldErrors).length === 0

  const handleCloseEntryPointModal = () => {
    if (isSavingEntryPoint) return
    setShowEntryPointModal(false)
    setSubmitError('')
    setEntryPointValidationAttempted(false)
  }

  const handleShowEntryPointModal = (existingEntryPoint) => {
    setSubmitError('')
    setEntryPointValidationAttempted(false)
    setEntryPoint(
      existingEntryPoint
        ? {
            id: existingEntryPoint.id,
            name: existingEntryPoint.name,
            venueId: existingEntryPoint.venueId,
          }
        : EMPTY_ENTRY_POINT
    )
    setShowEntryPointModal(true)
  }

  const handleCloseScannerModal = () => {
    if (isSavingScanner) return
    setShowScannerModal(false)
    setSubmitError('')
    setScannerValidationAttempted(false)
  }

  const handleShowScannerModal = (existingScanner) => {
    setSubmitError('')
    setScannerValidationAttempted(false)
    setScanner(
      existingScanner
        ? {
            id: existingScanner.id,
            name: `${existingScanner.firstName} ${existingScanner.lastName}`.trim(),
            firstName: existingScanner.firstName,
            lastName: existingScanner.lastName,
            email: existingScanner.email,
            password: '',
            entry_point_id: existingScanner.entryPoint?.id || '',
          }
        : EMPTY_SCANNER
    )
    setShowScannerModal(true)
  }

  const loadEntryPoints = (orgUUID) =>
    getEntryPoints(orgUUID)
      .then((res) => {
        setEntryPoints(res.data)
      })
      .catch((err) => console.error(err))

  const loadScanners = (orgUUID) =>
    getScanners(orgUUID)
      .then((res) => {
        setScanners(res.data)
      })
      .catch((err) => console.error(err))

  const loadVenues = (orgUUID) =>
    getOrgVenues(orgUUID)
      .then((res) => {
        setVenues(res.data || [])
      })
      .catch((err) => console.error(err))

  const loadAll = (orgUUID) => {
    showLoading()
    Promise.all([
      loadEntryPoints(orgUUID),
      loadScanners(orgUUID),
      loadVenues(orgUUID),
    ])
      .catch((err) => console.error(err))
      .finally(() => hideLoading())
  }

  const handleEntryPointChange = (e) => {
    const { name, value } = e.target
    setEntryPoint((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleScannerChange = (e) => {
    const { name, value } = e.target
    setScanner((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleScannerNameChange = (e) => {
    const name = e.target.value
    const { firstName, lastName } = splitScannerName(name)
    const email = buildScannerEmail(name, organization?.name)

    setScanner((prevState) => ({
      ...prevState,
      name,
      firstName,
      lastName,
      email,
    }))
  }

  const handleEntryPointSubmit = () => {
    setSubmitError('')
    setEntryPointValidationAttempted(true)

    if (!isEntryPointFormValid) {
      return
    }

    setIsSavingEntryPoint(true)
    createOrEditEntryPoint({
      entryPoint: {
        ...entryPoint,
        organizationUUID: organization.uuid,
      },
    })
      .then(() => {
        loadEntryPoints(organization.uuid)
        setShowEntryPointModal(false)
        setSubmitError('')
        setEntryPointValidationAttempted(false)
      })
      .catch((err) => {
        console.error(err)
        setSubmitError(
          err?.response?.data?.error?.message ||
            'Unable to save this entry point. Please try again.'
        )
      })
      .finally(() => setIsSavingEntryPoint(false))
  }

  const handleScannerSubmit = () => {
    setSubmitError('')
    setScannerValidationAttempted(true)

    if (!isScannerFormValid) {
      return
    }

    setIsSavingScanner(true)
    createOrEditScanner({
      scanner: {
        ...scanner,
        organizationUUID: organization.uuid,
      },
    })
      .then(() => {
        loadScanners(organization.uuid)
        setShowScannerModal(false)
        setSubmitError('')
        setScannerValidationAttempted(false)
      })
      .catch((err) => {
        console.error(err)
        setSubmitError(
          err?.response?.data?.error?.message ||
            'Unable to save this scanner. Please try again.'
        )
      })
      .finally(() => setIsSavingScanner(false))
  }

  useEffect(() => {
    if (!orgPermissionsReady && !isSuperAdmin) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    if (!organization?.uuid) return

    loadAll(organization.uuid)
  }, [orgPermissionsReady, hasPermission, isSuperAdmin, organization?.uuid])

  if (!orgPermissionsReady && !isSuperAdmin) {
    return <PageLoadingContainer />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  if (isLoading) {
    return <PageLoadingContainer />
  }

  return (
    <>
      <section className="wrapper">
        <header className="section-header">
          <div className="section-heading section-heading--secondary">
            <h1>Scanners</h1>
          </div>
          <p className="section-header-desc">
            Manage entry points and scanner accounts for the check-in app.
          </p>
        </header>
        <Card body className="card--sm">
          <Tab.Container
            defaultActiveKey={key}
            activeKey={key}
            onSelect={(k) => setKey(k)}
          >
            <div className="d-flex mb-2">
              <Nav as="ul" variant="pills" className="w-max-content">
                <Nav.Item as="li">
                  <Nav.Link
                    as="button"
                    eventKey="entry-points"
                    className="text-nowrap"
                  >
                    Entry Points
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li">
                  <Nav.Link as="button" eventKey="scanners">
                    Scanners
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>
            <Tab.Content>
              <Tab.Pane unmountOnExit={true} eventKey="entry-points">
                <EntryPointsTable
                  entryPoints={entryPoints}
                  handleShowEntryPointModal={handleShowEntryPointModal}
                />
              </Tab.Pane>
              <Tab.Pane unmountOnExit={true} eventKey="scanners">
                <ScannersTable
                  scanners={scanners}
                  handleShowScannerModal={handleShowScannerModal}
                />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card>
      </section>

      <Modal
        show={showEntryPointModal}
        onHide={handleCloseEntryPointModal}
        backdrop={isSavingEntryPoint ? 'static' : true}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditingEntryPoint ? 'Edit Entry Point' : 'Add Entry Point'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {submitError ? (
            <Alert variant="danger" className="mb-3">
              {submitError}
            </Alert>
          ) : null}
          <Form noValidate onSubmit={(e) => e.preventDefault()}>
            <Form.Group className="mb-3" controlId="name">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter entry point name"
                name="name"
                value={entryPoint.name}
                onChange={handleEntryPointChange}
                isInvalid={
                  entryPointValidationAttempted &&
                  Boolean(entryPointFieldErrors.name)
                }
                required
              />
              <Form.Control.Feedback type="invalid">
                {entryPointFieldErrors.name}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="venueId">
              <Form.Label>Venue</Form.Label>
              <Form.Select
                aria-label="venue"
                required
                value={entryPoint.venueId}
                onChange={handleEntryPointChange}
                name="venueId"
                isInvalid={
                  entryPointValidationAttempted &&
                  Boolean(entryPointFieldErrors.venueId)
                }
              >
                <option value="">Select a venue</option>
                {venues.map((venue) => (
                  <option key={venue.id} value={venue.id}>
                    {venue.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {entryPointFieldErrors.venueId}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={handleCloseEntryPointModal}
            disabled={isSavingEntryPoint}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => handleEntryPointSubmit()}
            disabled={
              isSavingEntryPoint ||
              (entryPointValidationAttempted && !isEntryPointFormValid)
            }
          >
            {isSavingEntryPoint ? <Spinner /> : 'Submit'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showScannerModal}
        onHide={handleCloseScannerModal}
        backdrop={isSavingScanner ? 'static' : true}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditingScanner ? 'Edit Scanner' : 'Add Scanner'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {submitError ? (
            <Alert variant="danger" className="mb-3">
              {submitError}
            </Alert>
          ) : null}
          <Form noValidate onSubmit={(e) => e.preventDefault()}>
            {isEditingScanner ? (
              <>
                <Form.Group className="mb-3" controlId="firstName">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter first name"
                    name="firstName"
                    value={scanner.firstName}
                    onChange={handleScannerChange}
                    isInvalid={
                      scannerValidationAttempted &&
                      Boolean(scannerFieldErrors.firstName)
                    }
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {scannerFieldErrors.firstName}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" controlId="lastName">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter last name"
                    name="lastName"
                    value={scanner.lastName}
                    onChange={handleScannerChange}
                    isInvalid={
                      scannerValidationAttempted &&
                      Boolean(scannerFieldErrors.lastName)
                    }
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {scannerFieldErrors.lastName}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" controlId="email">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter email address"
                    name="email"
                    value={scanner.email}
                    onChange={handleScannerChange}
                    isInvalid={
                      scannerValidationAttempted &&
                      Boolean(scannerFieldErrors.email)
                    }
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {scannerFieldErrors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </>
            ) : (
              <>
                <Form.Group className="mb-3" controlId="name">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter scanner name"
                    name="name"
                    value={scanner.name}
                    onChange={handleScannerNameChange}
                    isInvalid={
                      scannerValidationAttempted &&
                      Boolean(scannerFieldErrors.name)
                    }
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {scannerFieldErrors.name}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" controlId="email">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={scanner.email}
                    readOnly
                  />
                  <Form.Text muted>
                    Generated from the scanner name and organization.
                  </Form.Text>
                  {scannerValidationAttempted &&
                  scannerFieldErrors.email &&
                  !scannerFieldErrors.name ? (
                    <div className="invalid-feedback d-block">
                      {scannerFieldErrors.email}
                    </div>
                  ) : null}
                </Form.Group>
              </>
            )}
            <Form.Group className="mb-3" controlId="password">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                placeholder={
                  isEditingScanner
                    ? 'Leave blank to keep current password'
                    : 'Enter password'
                }
                name="password"
                value={scanner.password}
                onChange={handleScannerChange}
                isInvalid={
                  scannerValidationAttempted &&
                  Boolean(scannerFieldErrors.password)
                }
                required={!isEditingScanner}
              />
              <Form.Control.Feedback type="invalid">
                {scannerFieldErrors.password}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="entry_point_id">
              <Form.Label>Entry Point</Form.Label>
              <Form.Select
                aria-label="entry point"
                required
                value={scanner.entry_point_id}
                onChange={handleScannerChange}
                name="entry_point_id"
                isInvalid={
                  scannerValidationAttempted &&
                  Boolean(scannerFieldErrors.entry_point_id)
                }
              >
                <option value="">Select an entry point</option>
                {entryPoints.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                    {option.venueName ? ` (${option.venueName})` : ''}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {scannerFieldErrors.entry_point_id}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={handleCloseScannerModal}
            disabled={isSavingScanner}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => handleScannerSubmit()}
            disabled={
              isSavingScanner ||
              (scannerValidationAttempted && !isScannerFormValid)
            }
          >
            {isSavingScanner ? <Spinner /> : 'Submit'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
